import cn from 'classnames'
import { useCallback } from 'react'

import Deal from './Deal'
import DealsChart from './DealsChart'
import styles from './RevenueImpact.module.scss'
import { parseDealAmount, formatDealAmount } from './utils'

import { DealFragment } from '~generated/operations'
import Box from '~lib/ui-kit/Box'
import Col from '~lib/ui-kit/Col'
import Collapse from '~lib/ui-kit/Collapse'
import Row from '~lib/ui-kit/Row'
import Text from '~lib/ui-kit/Text'

interface RevenueImpactProps {
  deals?: DealFragment[]
  showDeals?: boolean
  className?: string
  onDealsChange?: (deals: DealFragment[]) => void
}

function RevenueImpact({ deals = [], showDeals, onDealsChange, className }: RevenueImpactProps) {
  const sum = deals.reduce((acc, deal) => acc + parseDealAmount(deal.amount), 0)

  const handleDealRemove = useCallback(
    (dealId: string) => {
      const filteredDeals = deals.filter(({ id }) => id !== dealId)
      onDealsChange?.(filteredDeals)
    },
    [deals, onDealsChange]
  )

  return (
    <div className={cn(styles.root, className)}>
      <Box className="p-2-5 mb-1" variant="light" borderRadius="sm">
        <Row align="center" justify="space-between" className="mb-1">
          <Col>
            <Text variant="secondary" bold>
              {deals.length} Deals impacted
            </Text>
          </Col>

          <Col>
            <Text variant="success">{formatDealAmount(sum)}</Text>
          </Col>
        </Row>

        <DealsChart deals={deals} />
      </Box>

      <Collapse opened={showDeals} innerClassName={styles.deals}>
        {deals.map((deal) => {
          return <Deal key={deal.id} deal={deal} onDealRemove={handleDealRemove} />
        })}
      </Collapse>
    </div>
  )
}

export default RevenueImpact
